'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

import { EssayPlanItem, BodyParagraphType } from '@/types/writing';

interface UseEssayPlanStateProps {
  initialEssayPlan?: EssayPlanItem[];
  initialEssayId?: string | null;
}

const DEFAULT_PLAN: EssayPlanItem[] = [
  { label: 'Position', type: undefined, content: '' },
  { label: 'Body Paragraph 1', type: undefined, content: '' },
  { label: 'Body Paragraph 2', type: undefined, content: '' },
];

export function useEssayPlanState({
  initialEssayPlan,
  initialEssayId,
}: UseEssayPlanStateProps) {
  const getInitialPlan = () => {
    if (initialEssayPlan && initialEssayPlan.length > 0) {
      return initialEssayPlan;
    }
    return DEFAULT_PLAN;
  };

  const [plan, setPlan] = useState<EssayPlanItem[]>(getInitialPlan());
  const prevEssayId = useRef<string | null | undefined>(initialEssayId);

  // State for accordion open/closed status
  const [isPlanOpen, setIsPlanOpen] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const storedState = localStorage.getItem('essayPlanOpen');
      return storedState ? JSON.parse(storedState) : true; // Default to open
    }
    return true; // Default to open for SSR or if window is not defined
  });

  // Effect to update localStorage when isPlanOpen changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('essayPlanOpen', JSON.stringify(isPlanOpen));
    }
  }, [isPlanOpen]);

  useEffect(() => {
    if (prevEssayId.current !== initialEssayId) {
      setPlan(getInitialPlan());
      prevEssayId.current = initialEssayId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEssayId, initialEssayPlan]); // Keep initialEssayPlan dependency for the getInitialPlan closure

  const handlePlanTypeChange = useCallback(
    (idx: number, value: BodyParagraphType | undefined) => {
      setPlan((items) =>
        items.map((item, i) => (i === idx ? { ...item, type: value } : item))
      );
    },
    []
  );

  const handlePlanContentChange = useCallback((idx: number, value: string) => {
    setPlan((items) =>
      items.map((item, i) => (i === idx ? { ...item, content: value } : item))
    );
  }, []);

  const addPlanItem = useCallback(() => {
    setPlan((items) => {
      const bodyCount = items.filter((item) =>
        item.label.startsWith('Body Paragraph')
      ).length;
      return [
        ...items,
        {
          label: `Body Paragraph ${bodyCount + 1}`,
          type: undefined,
          content: '',
        },
      ];
    });
  }, []);

  const removePlanItem = useCallback((idx: number) => {
    setPlan((items) => {
      if (idx === 0) return items;
      const newItems = items.filter((_, i) => i !== idx);
      let bodyCounter = 1;
      return newItems.map((item) => {
        if (item.label.startsWith('Body Paragraph')) {
          return { ...item, label: `Body Paragraph ${bodyCounter++}` };
        }
        return item;
      });
    });
  }, []);

  return {
    plan,
    setPlan,
    handlePlanTypeChange,
    handlePlanContentChange,
    addPlanItem,
    removePlanItem,
    isPlanOpen,
    setIsPlanOpen,
  };
}
